import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiTrendingUp,
  FiFileText,
  FiChevronRight,
  FiArrowLeft,
  FiCheck,
  FiClock,
  FiAlertTriangle,
  FiCalendar,
} from "react-icons/fi";
import Header from "../components/layout/Header";
import JuliaPanelAside from "../components/layout/JuliaPanelAside";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Button from "../components/ui/Button";
import { Obligation } from "../types";
import { obligationsApi } from "../api/obligations";
import { format } from "date-fns";
import { fr } from "date-fns/locale/fr";
import toast from "react-hot-toast";
import { CHARACTERS } from "../assets/characters";

// ── Composant carte sous-module ──────────────────────────────────────────────

const SubModuleCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
  onClick: () => void;
  badge?: string;
}> = ({ icon, title, description, color, onClick, badge }) => (
  <motion.div
    whileHover={{ y: -2, boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}
    onClick={onClick}
    style={{
      background: "var(--color-surface)",
      border: "1px solid var(--color-border-light)",
      borderRadius: "var(--radius-lg)",
      padding: "1.5rem",
      cursor: "pointer",
      display: "flex",
      alignItems: "flex-start",
      gap: "1rem",
      transition: "all 0.2s ease",
    }}
  >
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: "var(--radius-md)",
        background: color + "18",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <span style={{ color }}>{icon}</span>
    </div>
    <div style={{ flex: 1 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          marginBottom: "0.375rem",
        }}
      >
        <h3
          style={{
            fontSize: "var(--font-size-base)",
            fontWeight: 600,
            margin: 0,
          }}
        >
          {title}
        </h3>
        {badge && (
          <span
            style={{
              fontSize: "0.65rem",
              fontWeight: 600,
              padding: "0.125rem 0.5rem",
              borderRadius: "var(--radius-full)",
              background: color + "22",
              color,
            }}
          >
            {badge}
          </span>
        )}
      </div>
      <p
        style={{
          fontSize: "var(--font-size-sm)",
          color: "var(--color-text-secondary)",
          margin: 0,
        }}
      >
        {description}
      </p>
    </div>
    <FiChevronRight
      size={18}
      style={{ color: "var(--color-text-muted)", flexShrink: 0, marginTop: 4 }}
    />
  </motion.div>
);

// ── Ligne d'obligation ───────────────────────────────────────────────────────

const ObligationRow: React.FC<{
  obligation: Obligation;
  onComplete: (id: string) => void;
}> = ({ obligation, onComplete }) => {
  const statusColor =
    obligation.status === "completed"
      ? "var(--color-success)"
      : obligation.status === "overdue"
        ? "var(--color-danger)"
        : "var(--color-warning)";
  const statusLabel =
    obligation.status === "completed"
      ? "Complétée"
      : obligation.status === "overdue"
        ? "En retard"
        : obligation.status === "in_progress"
          ? "En cours"
          : "En attente";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "1rem",
        padding: "1rem 0",
        borderBottom: "1px solid var(--color-border-light)",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: "var(--radius-md)",
          background: statusColor + "18",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {obligation.status === "completed" ? (
          <FiCheck size={16} style={{ color: statusColor }} />
        ) : obligation.status === "overdue" ? (
          <FiAlertTriangle size={16} style={{ color: statusColor }} />
        ) : (
          <FiClock size={16} style={{ color: statusColor }} />
        )}
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            marginBottom: "0.25rem",
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontWeight: 600, fontSize: "var(--font-size-sm)" }}>
            {obligation.title}
          </span>
          <Badge
            variant={
              obligation.status === "completed"
                ? "success"
                : obligation.status === "overdue"
                  ? "danger"
                  : "warning"
            }
          >
            {statusLabel}
          </Badge>
        </div>
        <p
          style={{
            fontSize: "var(--font-size-sm)",
            color: "var(--color-text-secondary)",
            margin: "0 0 0.25rem",
          }}
        >
          {obligation.description}
        </p>
        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
          {obligation.due_date && (
            <span
              style={{
                fontSize: "var(--font-size-xs)",
                color: "var(--color-text-muted)",
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
              }}
            >
              <FiCalendar size={12} />{" "}
              {format(new Date(obligation.due_date), "d MMMM yyyy", {
                locale: fr,
              })}
            </span>
          )}
          {obligation.legal_reference && (
            <span
              style={{
                fontSize: "var(--font-size-xs)",
                color: "var(--color-primary)",
                fontStyle: "italic",
              }}
            >
              {obligation.legal_reference}
            </span>
          )}
        </div>
      </div>
      {obligation.status !== "completed" && (
        <Button
          variant="outline"
          onClick={() => onComplete(obligation.id)}
          style={{ flexShrink: 0 }}
        >
          Marquer complétée
        </Button>
      )}
    </div>
  );
};

// ── Sous-module : Seuils d'effectif ─────────────────────────────────────────

const ObligationsSeuils: React.FC<{
  obligations: Obligation[];
  onComplete: (id: string) => void;
}> = ({ obligations, onComplete }) => {
  const seuils = [
    {
      label: "11 salariés",
      description:
        "Mise en place du CSE, organisation des élections professionnelles",
      color: "var(--color-primary)",
    },
    {
      label: "20 salariés",
      description: "Obligation d'emploi des travailleurs handicapés (DOETH)",
      color: "var(--color-secondary)",
    },
    {
      label: "50 salariés",
      description:
        "Règlement intérieur obligatoire, NAO, BDESE, consultations CSE",
      color: "#0284C7",
    },
    {
      label: "250 salariés",
      description: "Index égalité hommes/femmes, bilan social",
      color: "var(--color-warning)",
    },
    {
      label: "300 salariés",
      description:
        "Obligations renforcées : commissions formation, égalité, participation",
      color: "var(--color-danger)",
    },
  ];
  const filtered = obligations.filter((o) => o.category === "seuil");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <div
        style={{
          padding: "0.75rem 1rem",
          background: "var(--color-primary)10",
          borderRadius: "var(--radius-md)",
          fontSize: "var(--font-size-sm)",
          color: "var(--color-primary)",
        }}
      >
        Ces obligations se mettent à jour automatiquement lorsque vous modifiez
        votre effectif dans « Ma structure ».
      </div>
      <Card header={<span>Seuils et obligations associées</span>}>
        {seuils.map((s, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              gap: "1rem",
              alignItems: "center",
              padding: "0.875rem 0",
              borderBottom:
                i < seuils.length - 1
                  ? "1px solid var(--color-border-light)"
                  : "none",
            }}
          >
            <div
              style={{
                minWidth: 90,
                padding: "0.375rem 0.75rem",
                borderRadius: "var(--radius-full)",
                background: s.color + "18",
                color: s.color,
                fontWeight: 700,
                fontSize: "var(--font-size-xs)",
                textAlign: "center",
              }}
            >
              {s.label}
            </div>
            <p
              style={{
                fontSize: "var(--font-size-sm)",
                color: "var(--color-text-secondary)",
                margin: 0,
                flex: 1,
              }}
            >
              {s.description}
            </p>
          </div>
        ))}
      </Card>
      {filtered.length > 0 && (
        <Card header={<span>Obligations en cours liées aux seuils</span>}>
          {filtered.map((o) => (
            <ObligationRow key={o.id} obligation={o} onComplete={onComplete} />
          ))}
        </Card>
      )}
    </div>
  );
};

// ── Sous-module : Obligations périodiques ────────────────────────────────────

const ObligationsPeriodiques: React.FC<{
  obligations: Obligation[];
  onComplete: (id: string) => void;
}> = ({ obligations, onComplete }) => {
  const config = [
    {
      titre: "Entretien professionnel",
      frequence: "Tous les 2 ans",
      description:
        "Obligatoire pour tout salarié en CDI ou CDD de 3 mois minimum.",
    },
    {
      titre: "NAO : Négociation Annuelle Obligatoire",
      frequence: "Annuelle",
      description: "Pour les entreprises disposant d'un délégué syndical.",
    },
    {
      titre: "Index égalité hommes/femmes",
      frequence: "Annuelle (avant le 1er mars)",
      description: "Obligatoire à partir de 50 salariés.",
    },
    {
      titre: "DOETH : Déclaration Emploi TH",
      frequence: "Annuelle",
      description: "Obligatoire à partir de 20 salariés.",
    },
    {
      titre: "Élections du CSE",
      frequence: "Tous les 4 ans",
      description: "Obligatoires à partir de 11 salariés.",
    },
    {
      titre: "Bilan de parcours salarié",
      frequence: "Tous les 6 ans",
      description:
        "Bilan du parcours professionnel lors de l'entretien professionnel.",
    },
    {
      titre: "Contribution formation professionnelle",
      frequence: "Annuelle",
      description: "Versement à l'OPCO selon la masse salariale.",
    },
  ];
  const filtered = obligations.filter((o) => o.category === "periodique");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <Card header={<span>Calendrier des obligations périodiques</span>}>
        {config.map((item, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              gap: "1rem",
              padding: "0.875rem 0",
              borderBottom:
                i < config.length - 1
                  ? "1px solid var(--color-border-light)"
                  : "none",
            }}
          >
            <div style={{ flex: 1 }}>
              <span
                style={{
                  fontWeight: 600,
                  fontSize: "var(--font-size-sm)",
                  display: "block",
                }}
              >
                {item.titre}
              </span>
              <span
                style={{
                  fontSize: "var(--font-size-xs)",
                  color: "var(--color-primary)",
                  fontWeight: 500,
                }}
              >
                {item.frequence}
              </span>
              <p
                style={{
                  fontSize: "var(--font-size-xs)",
                  color: "var(--color-text-secondary)",
                  margin: "0.125rem 0 0",
                }}
              >
                {item.description}
              </p>
            </div>
          </div>
        ))}
      </Card>
      {filtered.length > 0 ? (
        <Card header={<span>Obligations périodiques en cours</span>}>
          {filtered.map((o) => (
            <ObligationRow key={o.id} obligation={o} onComplete={onComplete} />
          ))}
        </Card>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            color: "var(--color-text-muted)",
          }}
        >
          <FiCheck
            size={32}
            style={{ opacity: 0.3, marginBottom: "0.75rem" }}
          />
          <p style={{ fontSize: "var(--font-size-sm)" }}>
            Aucune obligation périodique en cours.
          </p>
        </div>
      )}
    </div>
  );
};

// ── Sous-module : Obligations documentaires ──────────────────────────────────

const ObligationsDocumentaires: React.FC<{
  obligations: Obligation[];
  onComplete: (id: string) => void;
}> = ({ obligations, onComplete }) => {
  const docs = [
    {
      titre: "DUERP",
      description:
        "Document Unique d'Évaluation des Risques Professionnels : obligatoire dès le 1er salarié, mise à jour annuelle.",
      lien: "Art. R4121-1 CT",
    },
    {
      titre: "Registre unique du personnel",
      description:
        "Liste chronologique de toutes les personnes ayant travaillé dans l'entreprise. Conservé 5 ans après départ.",
      lien: "Art. L1221-13 CT",
    },
    {
      titre: "Affichages obligatoires",
      description:
        "Inspection du travail, médecin du travail, horaires, lutte contre les discriminations, harcèlement...",
      lien: "Art. L1223-1 CT",
    },
    {
      titre: "Règlement intérieur",
      description:
        "Obligatoire à partir de 50 salariés. Déposé à l'inspection du travail et au conseil des prud'hommes.",
      lien: "Art. L1311-1 CT",
    },
    {
      titre: "Documents RGPD",
      description:
        "Registre des traitements, politique de confidentialité, procédures de gestion des données personnelles.",
      lien: "RGPD art. 30",
    },
  ];
  const filtered = obligations.filter((o) => o.category === "documentaire");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <Card header={<span>Documents RH obligatoires</span>}>
        {docs.map((doc, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              gap: "1rem",
              padding: "0.875rem 0",
              borderBottom:
                i < docs.length - 1
                  ? "1px solid var(--color-border-light)"
                  : "none",
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "var(--radius-md)",
                background: "var(--color-primary)15",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <FiFileText size={16} style={{ color: "var(--color-primary)" }} />
            </div>
            <div>
              <span
                style={{
                  fontWeight: 600,
                  fontSize: "var(--font-size-sm)",
                  display: "block",
                  marginBottom: "0.25rem",
                }}
              >
                {doc.titre}
              </span>
              <p
                style={{
                  fontSize: "var(--font-size-sm)",
                  color: "var(--color-text-secondary)",
                  margin: "0 0 0.25rem",
                }}
              >
                {doc.description}
              </p>
              <span
                style={{
                  fontSize: "var(--font-size-xs)",
                  color: "var(--color-primary)",
                  fontStyle: "italic",
                }}
              >
                {doc.lien}
              </span>
            </div>
          </div>
        ))}
      </Card>
      {filtered.length > 0 && (
        <Card header={<span>Documents à produire ou mettre à jour</span>}>
          {filtered.map((o) => (
            <ObligationRow key={o.id} obligation={o} onComplete={onComplete} />
          ))}
        </Card>
      )}
    </div>
  );
};

// ── Page principale ───────────────────────────────────────────────────────────

type SubModule = "seuils" | "periodiques" | "documentaires" | null;

const DEMO_OBLIGATIONS: Obligation[] = [
  {
    id: "1",
    company_id: "1",
    title: "Déclaration DSN mensuelle",
    description: "Déclaration sociale nominative",
    category: "periodique",
    due_date: "2026-05-15",
    status: "pending",
    priority: "high",
    legal_reference: "Art. R243-14 CSS",
    created_at: "",
  },
  {
    id: "2",
    company_id: "1",
    title: "Mise à jour du registre du personnel",
    description: "Registre unique du personnel",
    category: "documentaire",
    due_date: "2026-04-28",
    status: "overdue",
    priority: "high",
    legal_reference: "Art. L1221-13 CT",
    created_at: "",
  },
  {
    id: "3",
    company_id: "1",
    title: "Visite médicale périodique",
    description: "3 salariés concernés",
    category: "periodique",
    due_date: "2026-05-20",
    status: "pending",
    priority: "medium",
    legal_reference: "Art. R4624-16 CT",
    created_at: "",
  },
  {
    id: "4",
    company_id: "1",
    title: "Index égalité hommes/femmes",
    description: "Publication de l'index égalité",
    category: "seuil",
    due_date: "2026-03-01",
    status: "completed",
    priority: "high",
    legal_reference: "Art. L1142-8 CT",
    created_at: "",
  },
  {
    id: "5",
    company_id: "1",
    title: "Mise à jour DUERP",
    description: "Document unique d'évaluation des risques",
    category: "documentaire",
    due_date: "2026-06-01",
    status: "pending",
    priority: "medium",
    legal_reference: "Art. R4121-1 CT",
    created_at: "",
  },
];

const ObligationsPage: React.FC = () => {
  const [activeModule, setActiveModule] = useState<SubModule>(null);
  const [obligations, setObligations] = useState<Obligation[]>([]);

  useEffect(() => {
    obligationsApi
      .list({})
      .then((res) => {
        const d = res.data as any;
        const data = d.obligations || d.data || [];
        setObligations(data.length > 0 ? data : DEMO_OBLIGATIONS);
      })
      .catch(() => setObligations(DEMO_OBLIGATIONS));
  }, []);

  const handleComplete = async (id: string) => {
    try {
      await obligationsApi.complete(id);
      setObligations((prev) =>
        prev.map((o) => (o.id === id ? { ...o, status: "completed" } : o)),
      );
      toast.success("Obligation marquée comme complétée");
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const pending = obligations.filter(
    (o) => o.status === "pending" || o.status === "overdue",
  ).length;

  const countByCategory = (cat: string) =>
    obligations.filter((o) => o.category === cat && o.status !== "completed")
      .length;

  const subModules = [
    {
      id: "seuils" as SubModule,
      icon: <FiTrendingUp size={24} />,
      title: "Obligations liées aux seuils d'effectif",
      description:
        "11, 20, 50, 250, 300 salariés : mises à jour automatiquement selon votre effectif déclaré dans Ma structure",
      color: "var(--color-primary)",
      count: countByCategory("seuil"),
    },
    {
      id: "periodiques" as SubModule,
      icon: <FiCalendar size={24} />,
      title: "Obligations périodiques",
      description:
        "Entretien professionnel (2 ans), NAO, Index égalité, Élections CSE (4 ans), bilan 6 ans, contribution formation",
      color: "#0284C7",
      count: countByCategory("periodique"),
    },
    {
      id: "documentaires" as SubModule,
      icon: <FiFileText size={24} />,
      title: "Obligations documentaires",
      description:
        "DUERP, Registre unique du personnel, Affichages obligatoires, Règlement intérieur, Documents RGPD",
      color: "var(--color-warning)",
      count: countByCategory("documentaire"),
    },
  ];

  const titles: Record<string, string> = {
    seuils: "Obligations liées aux seuils d'effectif",
    periodiques: "Obligations périodiques",
    documentaires: "Obligations documentaires",
  };

  return (
    <div>
      <Header
        title={
          activeModule
            ? `Obligations employeur : ${titles[activeModule]}`
            : "Obligations employeur"
        }
        subtitle={
          activeModule
            ? undefined
            : `${pending} obligation${pending > 1 ? "s" : ""} en attente ou en retard`
        }
      />

      {!activeModule && (
        <div style={{ padding: "0 2rem" }}>
          <div
            style={{
              marginTop: "0.75rem",
              background: "linear-gradient(135deg, #ECFEFF 0%, #E0F2FE 100%)",
              borderRadius: "1rem",
              padding: "0.85rem 1.25rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1rem",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: "0.92rem",
                color: "var(--color-text-secondary)",
                maxWidth: 480,
                lineHeight: 1.5,
              }}
            >
              Julia vous aide à prioriser les obligations selon votre effectif
              et votre maturité RH.
            </p>
            <img
              src={CHARACTERS.manager}
              alt=""
              aria-hidden
              style={{ width: 88, height: "auto", flexShrink: 0 }}
            />
          </div>
        </div>
      )}

      <div style={{ padding: "1.5rem 2rem" }}>
        {!activeModule ? (
          <div
            style={{
              display: "flex",
              gap: "1.5rem",
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                flex: "1 1 320px",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                maxWidth: 860,
              }}
            >
              {subModules.map((mod) => (
                <SubModuleCard
                  key={mod.id}
                  icon={mod.icon}
                  title={mod.title}
                  description={mod.description}
                  color={mod.color}
                  badge={mod.count > 0 ? `${mod.count} en attente` : undefined}
                  onClick={() => setActiveModule(mod.id)}
                />
              ))}
            </div>
            <JuliaPanelAside
              moduleName="Obligations employeur"
              contextMessage={`Vous avez ${pending} obligation${pending > 1 ? "s" : ""} en attente ou en retard. Je peux vous guider pour les traiter par catégorie.`}
              pendingTasks={pending > 0 ? Math.min(pending, 9) : 0}
            />
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeModule}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <div style={{ marginBottom: "1.5rem" }}>
                <button
                  onClick={() => setActiveModule(null)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    fontSize: "var(--font-size-sm)",
                    color: "var(--color-text-secondary)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  <FiArrowLeft size={16} /> Retour aux sous-modules
                </button>
              </div>
              {activeModule === "seuils" && (
                <ObligationsSeuils
                  obligations={obligations}
                  onComplete={handleComplete}
                />
              )}
              {activeModule === "periodiques" && (
                <ObligationsPeriodiques
                  obligations={obligations}
                  onComplete={handleComplete}
                />
              )}
              {activeModule === "documentaires" && (
                <ObligationsDocumentaires
                  obligations={obligations}
                  onComplete={handleComplete}
                />
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default ObligationsPage;
